import type { FieldReannotationParams, MissingFieldCountResponse } from "../../../lib/corpusApi";
import type { CorpusScanBadge } from "./corpus_scan_contract";
import { fieldLabel } from "../../../lib/scorecardFieldUtils";

/** The result of a missing-field scan as its card shows it. */
export interface MissingFieldScanView {
  /** What was found, "7 of 40 documents have no peer review label". */
  summary: string;
  /** What the launch button says; null when no document is missing the field. */
  launchLabel: string | null;
}

/** What a missing-field scan found, in a word for its card. */
export function describeMissingFieldBadge({ missing }: MissingFieldCountResponse): CorpusScanBadge {
  return missing > 0
    ? { label: `${missing} missing`, tone: "attention" }
    : { label: "None missing", tone: "ok" };
}

/** The wording of the confirmation dialog shown before a field re-annotation starts. */
export interface FieldReannotationConfirmation {
  title: string;
  message: string;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function lowerFieldLabel(field: MissingFieldCountResponse["field"]): string {
  return fieldLabel(field).toLowerCase();
}

/** Turns a missing-field count into the words of its card. */
export function describeMissingFieldScan({
  field,
  scanned,
  missing,
}: MissingFieldCountResponse): MissingFieldScanView {
  return {
    summary: `${missing} of ${pluralize(scanned, "document")} ${missing === 1 ? "has" : "have"} no ${lowerFieldLabel(field)} label`,
    launchLabel: missing > 0 ? `Re-annotate ${pluralize(missing, "document")}` : null,
  };
}

/** The wording of the confirmation before a field re-annotation job starts. */
export function describeFieldReannotationConfirmation({
  field,
  missing,
}: MissingFieldCountResponse): FieldReannotationConfirmation {
  return {
    title: `Re-annotate ${lowerFieldLabel(field)}?`,
    message:
      `Starts a batch job that re-runs the AI annotation of ${lowerFieldLabel(field)} on the ` +
      `${pluralize(missing, "document")} that have no label for it. Documents a person has ` +
      `decided on are not touched. It spends extraction budget.`,
  };
}

/** The job that re-annotates a field on every document missing it. */
export function buildMissingFieldReannotation({
  field,
}: MissingFieldCountResponse): FieldReannotationParams {
  return { scope: "all", field, annotationState: "missing" };
}
